import { Atom } from "@effect-atom/atom";
import { useAtomSet, useAtomValue } from "@effect-atom/atom-react";
import { Effect, Option } from "effect";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { Button } from "~/components/ui/button.tsx";
import { AppStoreConfigLive, loadAppStoreConfig } from "~/config/app-store.ts";
import type { AppStoreConfig } from "~/config/app-store.ts";
import { checkWaitlistStatus } from "~/lib/waitlist.ts";
import { WaitlistForm } from "./WaitlistForm.tsx";

interface AppStoreButtonProps {
  variant?: "ios" | "android";
  size?: "default" | "lg";
  className?: string;
}

/**
 * Atom to track if user has clicked the waitlist CTA
 */
const showFormAtom = Atom.make(false);

/**
 * Atom to track if user has joined the waitlist (global state)
 */
const hasJoinedWaitlistAtom = Atom.make(false);

/**
 * Atom to track initialization status
 */
const configInitializedAtom = Atom.make(false);

/**
 * Atom to load app store config
 */
const configAtom = Atom.make<AppStoreConfig | null>(null);

export const AppStoreButton: React.FC<AppStoreButtonProps> = ({
  className = "",
  size = "lg",
  variant = "ios",
}) => {
  const isIOS = variant === "ios";
  const showForm = useAtomValue(showFormAtom);
  const setShowForm = useAtomSet(showFormAtom);
  const hasJoinedWaitlist = useAtomValue(hasJoinedWaitlistAtom);
  const setHasJoinedWaitlist = useAtomSet(hasJoinedWaitlistAtom);
  const config = useAtomValue(configAtom);
  const setConfig = useAtomSet(configAtom);
  const configInitialized = useAtomValue(configInitializedAtom);
  const setConfigInitialized = useAtomSet(configInitializedAtom);

  // Load config and check waitlist status on first render
  if (!configInitialized) {
    setConfigInitialized(true);
    Effect.runPromise(
      loadAppStoreConfig.pipe(
        Effect.provide(AppStoreConfigLive),
        Effect.tap((cfg) => Effect.sync(() => setConfig(cfg))),
        Effect.catchAll((error) =>
          Effect.sync(() => {
            console.error("Failed to load app store config:", error);
            setConfig({ mode: "waitlist", iosUrl: Option.none(), androidUrl: Option.none() });
          })
        ),
      ),
    );
    const isOnWaitlist = checkWaitlistStatus();
    if (isOnWaitlist) {
      setHasJoinedWaitlist(true);
    }
  }

  if (!config) {
    // Loading state
    return null;
  }

  // Waitlist mode: Show single CTA that becomes a form
  // Only render for iOS variant to avoid duplicates
  if (config.mode === "waitlist") {
    if (variant === "android") {
      return null; // Hide Android button in waitlist mode
    }

    // If user has already joined, show success message
    if (hasJoinedWaitlist) {
      return (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <p className="text-white text-lg font-medium mb-2">
            🎉 You're on the list!
          </p>
          <p className="text-zinc-400 text-sm">
            We'll notify you when FlirtMachine launches.
          </p>
        </motion.div>
      );
    }

    return (
      <AnimatePresence mode="wait">
        {!showForm ?
          (
            <motion.div
              key="cta"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                onClick={() => setShowForm(true)}
                size={size}
                className={`h-16 px-8 text-lg transition-all rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(220,20,60,0.4)] hover:scale-105 ${className}`}
              >
                Join the Waitlist
              </Button>
            </motion.div>
          ) :
          (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="w-full"
            >
              <WaitlistForm size={size} className={className} onJoined={() => setHasJoinedWaitlist(true)} />
            </motion.div>
          )}
      </AnimatePresence>
    );
  }

  // Live mode: Show app store buttons with links
  const url = isIOS
    ? Option.getOrElse(config.iosUrl, () => "#")
    : Option.getOrElse(config.androidUrl, () => "#");

  return (
    <Button
      asChild
      size={size}
      variant={isIOS ? "default" : "outline"}
      className={`h-16 px-8 text-lg transition-all rounded-xl flex items-center gap-3 ${
        isIOS
          ? "bg-white text-black hover:bg-zinc-200"
          : "border-white/20 bg-white/5 hover:bg-white/10 text-white"
      } ${className}`}
    >
      <a href={url} target="_blank" rel="noopener noreferrer">
        {isIOS ?
          (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74 1.18 0 2.21-.84 3.67-.84 1.54 0 2.72.65 3.43 1.69-3.1 1.87-2.55 6.57.58 7.92-.67 1.69-1.59 3.25-2.76 3.46zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
            </svg>
          ) :
          (
            <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-1.61-.78V2.605c0-.77.82-1.26 1.609-.791zm11.31 11.055l4.196 4.196-3.515 2.011c-.57.326-1.27.126-1.596-.411l-1.198-1.198 2.113-4.598zm5.786-1.583l-4.995-4.995-2.258 4.912 5.37 2.685c.64.32 1.41-.05 1.58-.74.1-.41-.05-.85-.35-1.16l.653-1.702zm-6.404-5.28L12.077 3.78l3.845-2.2c.57-.327 1.28-.12 1.602.42.32.54.14 1.24-.4 1.59l-2.823 2.416z" />
            </svg>
          )}
        <div className="flex flex-col items-start leading-none">
          <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">
            {isIOS ? "Download on the" : "Get it on"}
          </span>
          <span className="text-sm font-bold">{isIOS ? "App Store" : "Google Play"}</span>
        </div>
      </a>
    </Button>
  );
};
